// src/services/user/userProfileService.js

import { auth, db } from '../../firebase';
import {
  doc,
  getDoc,
  getDocs,
  setDoc,
  query,
  where,
  limit,
  onSnapshot,
  collection,
  serverTimestamp,
} from 'firebase/firestore';
import { UserProfile } from '../../models/userModels/userProfile';

class UserProfileService {
  // ── Singleton ──────────────────────────────────────────────────────────────

  constructor() {
    this._db = db;
    this._auth = auth;
    this._collection = 'users';
    this._cachedProfile = null;
  }

  static getInstance() {
    if (!UserProfileService._instance) {
      UserProfileService._instance = new UserProfileService();
    }
    return UserProfileService._instance;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // FETCH
  // ─────────────────────────────────────────────────────────────────────────

  async getCurrentUserProfile({ forceRefresh = false } = {}) {
    if (this._cachedProfile && !forceRefresh) return this._cachedProfile;

    const firebaseUser = this._auth.currentUser;
    if (!firebaseUser) return null;

    return this.getProfileByUid(firebaseUser.uid);
  }

  async getProfileByUid(uid) {
    try {
      const docRef = doc(this._db, this._collection, uid);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) return null;

      const profile = UserProfile.fromMap(uid, docSnap.data());

      // Cache if it's the current user
      if (this._auth.currentUser?.uid === uid) {
        this._cachedProfile = profile;
      }

      return profile;
    } catch (error) {
      console.error('getProfileByUid error:', error);
      return null;
    }
  }

  async getProfileByPhone(phone) {
    try {
      const q = query(
        collection(this._db, this._collection),
        where('phone', '==', phone),
        limit(1)
      );
      const querySnap = await getDocs(q);

      if (querySnap.empty) return null;

      const doc = querySnap.docs[0];
      return UserProfile.fromMap(doc.id, doc.data());
    } catch (error) {
      console.error('getProfileByPhone error:', error);
      return null;
    }
  }

  async getProfileByUsername(username) {
    try {
      const q = query(
        collection(this._db, this._collection),
        where('username', '==', username),
        limit(1)
      );
      const querySnap = await getDocs(q);

      if (querySnap.empty) return null;

      const doc = querySnap.docs[0];
      return UserProfile.fromMap(doc.id, doc.data());
    } catch (error) {
      console.error('getProfileByUsername error:', error);
      return null;
    }
  }

  async getMultipleProfiles(uids) {
    if (!uids || uids.length === 0) return [];

    try {
      // Firestore doesn't support 'in' with more than 10 items
      // For larger lists, we need multiple queries
      const results = [];
      const chunkSize = 10;

      for (let i = 0; i < uids.length; i += chunkSize) {
        const chunk = uids.slice(i, i + chunkSize);
        const q = query(
          collection(this._db, this._collection),
          where('uid', 'in', chunk)
        );
        const querySnap = await getDocs(q);

        for (const doc of querySnap.docs) {
          results.push(UserProfile.fromMap(doc.id, doc.data()));
        }
      }

      return results;
    } catch (error) {
      console.error('getMultipleProfiles error:', error);
      return [];
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // STREAM (real-time updates)
  // ─────────────────────────────────────────────────────────────────────────

  getCurrentUserProfileStream() {
    const firebaseUser = this._auth.currentUser;
    if (!firebaseUser) {
      return new Promise((resolve) => resolve(null));
    }

    return new Promise((resolve) => {
      const unsubscribe = onSnapshot(
        doc(this._db, this._collection, firebaseUser.uid),
        (docSnap) => {
          if (!docSnap.exists()) {
            resolve(null);
            return;
          }
          const profile = UserProfile.fromMap(docSnap.id, docSnap.data());
          this._cachedProfile = profile;
          resolve(profile);
        },
        (error) => {
          console.error('getCurrentUserProfileStream error:', error);
          resolve(null);
        }
      );
      return unsubscribe;
    });
  }

  getProfileStream(uid, callback) {
    return onSnapshot(
      doc(this._db, this._collection, uid),
      (docSnap) => {
        if (!docSnap.exists()) {
          callback(null);
          return;
        }
        const profile = UserProfile.fromMap(docSnap.id, docSnap.data());
        callback(profile);
      },
      (error) => {
        console.error('getProfileStream error:', error);
        callback(null);
      }
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // CACHE HELPERS
  // ─────────────────────────────────────────────────────────────────────────

  get cachedProfile() {
    return this._cachedProfile;
  }

  clearCache() {
    this._cachedProfile = null;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // UPDATE PROFILE
  // ─────────────────────────────────────────────────────────────────────────

  async updateProfile(uid, data) {
    try {
      const docRef = doc(this._db, this._collection, uid);
      await setDoc(docRef, {
        ...data,
        updatedAt: serverTimestamp(),
      }, { merge: true });

      // Update cache if it's the current user
      if (this._auth.currentUser?.uid === uid && this._cachedProfile) {
        this._cachedProfile = { ...this._cachedProfile, ...data };
      }

      return { success: true };
    } catch (error) {
      console.error('updateProfile error:', error);
      return { success: false, error: error.message };
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // DISPOSE
  // ─────────────────────────────────────────────────────────────────────────

  dispose() {
    this.clearCache();
  }
}

// ── Singleton export ─────────────────────────────────────────────────────────

const userProfileService = UserProfileService.getInstance();
export { userProfileService };
export default userProfileService;